import { useState, useEffect } from 'react';
import { Users, TrendingDown } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface SpotCounterProps {
  variant?: 'default' | 'compact' | 'inline';
  showProgressBar?: boolean;
}

export default function SpotCounter({ variant = 'default', showProgressBar = true }: SpotCounterProps) {
  const [spotsRemaining, setSpotsRemaining] = useState(5);
  const [totalSpots] = useState(100);
  const [spotsTaken, setSpotsTaken] = useState(95);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchSpotData();

    const channel = supabase
      .channel('spot_counter_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'spot_counter' },
        (payload) => {
          if (payload.new && typeof payload.new === 'object' && 'spots_remaining' in payload.new) {
            const newSpots = payload.new.spots_remaining as number;
            setSpotsRemaining(newSpots);
            setSpotsTaken(totalSpots - newSpots);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchSpotData = async () => {
    try {
      const { data, error } = await supabase
        .from('spot_counter')
        .select('spots_remaining, total_spots')
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setSpotsRemaining(data.spots_remaining);
        setSpotsTaken(data.total_spots - data.spots_remaining);
      }
    } catch (error) {
      console.error('Error fetching spot data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const percentage = (spotsTaken / totalSpots) * 100;
  const urgencyColor = spotsRemaining > 50 ? 'green' : spotsRemaining > 20 ? 'amber' : 'red';

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-32"></div>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className="inline-flex items-center gap-2">
        <Users className={`w-4 h-4 ${urgencyColor === 'red' ? 'text-red-500' : urgencyColor === 'amber' ? 'text-amber-500' : 'text-green-500'}`} />
        <span className="font-semibold">
          Bereits <span className={`${urgencyColor === 'red' ? 'text-red-600' : urgencyColor === 'amber' ? 'text-amber-600' : 'text-green-600'}`}>{spotsTaken}/{totalSpots}</span> Plätzen gebucht - Nur noch {spotsRemaining} verfügbar
        </span>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2 font-medium">
            <TrendingDown className={`w-4 h-4 ${urgencyColor === 'red' ? 'text-red-500 animate-pulse' : urgencyColor === 'amber' ? 'text-amber-500' : 'text-green-500'}`} />
            Noch {spotsRemaining} Plätze frei
          </span>
          <span className="text-gray-600">{spotsTaken}/{totalSpots}</span>
        </div>
        {showProgressBar && (
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full transition-all duration-700 ease-out ${
                urgencyColor === 'red'
                  ? 'bg-gradient-to-r from-red-500 to-red-600 animate-pulse'
                  : urgencyColor === 'amber'
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600'
                  : 'bg-gradient-to-r from-green-500 to-green-600'
              }`}
              style={{ width: `${percentage}%` }}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-4 shadow-md border-2 border-dashed border-gray-300 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            urgencyColor === 'red' ? 'bg-red-100' : urgencyColor === 'amber' ? 'bg-amber-100' : 'bg-green-100'
          }`}>
            <Users className={`w-5 h-5 ${
              urgencyColor === 'red' ? 'text-red-600' : urgencyColor === 'amber' ? 'text-amber-600' : 'text-green-600'
            }`} />
          </div>
          <div>
            <div className="text-sm text-gray-600">Bereits gebucht</div>
            <div className={`text-2xl font-bold ${
              urgencyColor === 'red' ? 'text-red-600' : urgencyColor === 'amber' ? 'text-amber-600' : 'text-green-600'
            }`}>
              {spotsTaken} <span className="text-sm text-gray-500">/ {totalSpots}</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">Nur noch {spotsRemaining} Plätze frei</div>
          </div>
        </div>
        {urgencyColor === 'red' && (
          <div className="text-right">
            <div className="text-xs font-bold text-red-600 uppercase animate-pulse">Fast ausverkauft!</div>
          </div>
        )}
      </div>

      {showProgressBar && (
        <div className="space-y-1">
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className={`h-full transition-all duration-700 ease-out ${
                urgencyColor === 'red'
                  ? 'bg-gradient-to-r from-red-500 to-red-600 animate-pulse'
                  : urgencyColor === 'amber'
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600'
                  : 'bg-gradient-to-r from-green-500 to-green-600'
              }`}
              style={{ width: `${percentage}%` }}
            />
          </div>
          <p className="text-xs text-gray-600 text-center">
            {urgencyColor === 'red'
              ? 'Nur noch wenige Plätze verfügbar!'
              : urgencyColor === 'amber'
              ? 'Limitiertes Angebot - Sichere dir deinen Platz!'
              : 'Begrenztes Angebot - Jetzt Aktionspreis sichern!'}
          </p>
        </div>
      )}
    </div>
  );
}
